/** Human-readable "last computed" label for portfolio / score freshness (DMD-1063 / DMD-1083). */
export function formatLastComputedLabel(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;

  const diffMs = Date.now() - date.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  let relative: string;
  if (diffMs < 0) relative = 'just now';
  else if (diffMins < 1) relative = 'just now';
  else if (diffMins < 60) relative = `${diffMins}m ago`;
  else if (diffHours < 24) relative = `${diffHours}h ago`;
  else if (diffDays === 1) relative = 'yesterday';
  else if (diffDays < 14) relative = `${diffDays}d ago`;
  else {
    relative = date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  return `Last computed ${relative}`;
}

export interface ScoreFreshness {
  /** True only when the API reports a live computation source. Never inferred from timestamp age. */
  isLive: boolean;
  /** False when the API sent no source — callers must not label the score Live or Prior. */
  hasSource: boolean;
  label: string;
  absoluteTitle: string | undefined;
}

/** Live vs prior score freshness, strictly from API-reported computation metadata (DMD-1158). */
export function resolveScoreFreshness(
  lastComputedAt: string | null | undefined,
  lastComputedSource?: string | null,
): ScoreFreshness {
  const source = String(lastComputedSource ?? '').trim().toLowerCase();
  const hasSource = source.length > 0;
  const isLive =
    hasSource &&
    (source.includes('live') ||
      source.includes('realtime') ||
      source.includes('real-time') ||
      source.includes('fresh') ||
      source === 'compute' ||
      source === 'computed');

  const relative = formatLastComputedLabel(lastComputedAt);
  const absoluteTitle = lastComputedAt
    ? (() => {
        const d = new Date(lastComputedAt);
        return Number.isNaN(d.getTime()) ? undefined : d.toLocaleString();
      })()
    : undefined;

  return {
    isLive,
    hasSource,
    label: relative ?? (hasSource ? (isLive ? 'Live score' : 'Prior computation') : ''),
    absoluteTitle,
  };
}
